import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import Toast from '@/components/base/Toast';
import { fetchSettings, submitModification } from '@/services/settingsService';
import type { LaundryModificationRequest, LaundryProfile, SettingsData } from '@/types';
import LanguageSection from './components/LanguageSection';
import LaundryDataSection from './components/LaundryDataSection';
import PasswordSection from './components/PasswordSection';
import RequestModificationModal from './components/RequestModificationModal';

type ToastState = { message: string; tone: 'success' | 'error' };
type SettingsTab = 'data' | 'security' | 'language';

const TAB_IDS: SettingsTab[] = ['data', 'security', 'language'];

export default function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab') as SettingsTab | null;
  const tab: SettingsTab = requestedTab && TAB_IDS.includes(requestedTab) ? requestedTab : 'data';

  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [modification, setModification] = useState<LaundryModificationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [requestOpen, setRequestOpen] = useState(false);
  const [highlightDurations, setHighlightDurations] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchSettings();
      setSettings(data);
      setModification(data.modification);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deep link from "Items & Services" → open the form and highlight the durations.
  const wantEdit = searchParams.get('edit') === '1';
  const wantDurations = searchParams.get('highlight') === 'durations';
  useEffect(() => {
    if (!loading && !error && settings && wantEdit) {
      setHighlightDurations(wantDurations);
      setRequestOpen(true);
    }
  }, [loading, error, settings, wantEdit, wantDurations]);

  const changeTab = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'data') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const handleSubmit = async (proposed: LaundryProfile) => {
    const created = await submitModification(proposed);
    setModification(created);
    setToast({ message: t('settings.toast.submitted'), tone: 'success' });
  };

  const tabs: TabItem[] = [
    { id: 'data', label: t('settings.section.laundry'), icon: 'ri-store-2-line' },
    { id: 'security', label: t('settings.section.security'), icon: 'ri-lock-password-line' },
    { id: 'language', label: t('settings.section.language'), icon: 'ri-translate-2' },
  ];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-foreground-500">{t('settings.subtitle')}</p>
      </header>

      <Tabs items={tabs} active={tab} onChange={changeTab} className="max-w-full overflow-x-auto lb-no-scrollbar" />

      {loading ? (
        <Card className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-20 w-20" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-56" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-24 w-full" />
        </Card>
      ) : error || !settings ? (
        <Card>
          <ErrorState
            title={t('settings.error')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={load}
          />
        </Card>
      ) : (
        <>
          <div key={tab} className="animate-fade-in space-y-5" role="tabpanel">
            {tab === 'data' ? (
              <LaundryDataSection
                profile={settings.profile}
                modification={modification}
                onRequestEdit={() => {
                  setHighlightDurations(false);
                  setRequestOpen(true);
                }}
              />
            ) : null}

            {tab === 'security' ? (
              <PasswordSection
                onSuccess={() => setToast({ message: t('settings.toast.password'), tone: 'success' })}
              />
            ) : null}

            {tab === 'language' ? <LanguageSection /> : null}
          </div>

          <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-foreground-500">
            <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('settings.demoNote')}
          </p>

          <RequestModificationModal
            open={requestOpen}
            profile={settings.profile}
            coverage={settings.coverage}
            highlightDurations={highlightDurations}
            onClose={() => setRequestOpen(false)}
            onSubmit={handleSubmit}
          />
        </>
      )}

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}